import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, isDevMode, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GuardsCheckEnd, Router } from '@angular/router';

import { SuiToastService } from '@semeris/ui';
import { differenceInMinutes } from 'date-fns';
import { environment } from 'projects/semeris/src/environments/environment';
import { filter, timer } from 'rxjs';

import { LocalStorageService } from './local-storage.service';
import { isEmbedded } from '../constants/embedded.regex';

const UPDATE_TOAST_KEY = 'app-version-update';
const SNOOZE_STORAGE_KEY = 'version-update-snoozed-at';
const BOUNDARY_RELOAD_SESSION_KEY = 'semeris-version-boundary-reload';
const POLL_DELAY_MS = 5_000;
const POLL_INTERVAL_MS = 5 * 60_000;
const SNOOZE_MINUTES = 60;

/** Path portion of a router URL — query and fragment stripped. */
function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private readonly http = inject(HttpClient);
  private readonly toastService = inject(SuiToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private isChecking = false;

  /** True once a newer build has been detected. Sticky — a reload is the only exit. */
  public readonly isUpdatePending = signal(false);

  public init() {
    timer(POLL_DELAY_MS, POLL_INTERVAL_MS)
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkForUpdate();
      });

    // Not redundant with `visibilitychange`: that misses re-focusing a window whose active tab is already ours.
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    };
    window.addEventListener('focus', onFocus);
    this.destroyRef.onDestroy(() => window.removeEventListener('focus', onFocus));

    this.watchRouteBoundaries();
  }

  public checkForUpdate() {
    if (this.isSnoozed()) {
      return;
    }

    // Already known — re-raise the prompt without spending another request.
    if (this.isUpdatePending()) {
      this.promptReload();
      return;
    }

    if (this.isChecking) {
      return;
    }
    this.isChecking = true;

    this.http
      .get<{
        version: string;
      }>(`/assets/version.json?rnd=${Date.now()}`)
      .subscribe({
        next: (response) => {
          this.isChecking = false;

          if (!response) {
            console.error('Failed to check for update.');
            return;
          }

          if (response.version !== environment.version && !isDevMode()) {
            this.isUpdatePending.set(true);
            this.promptReload();
            return;
          }

          // Confirmed current — re-arm the boundary reload for the next deploy.
          sessionStorage.removeItem(BOUNDARY_RELOAD_SESSION_KEY);
        },
        error: (err) => {
          this.isChecking = false;
          console.error('Failed to check for update.');
          console.error(err);
        },
      });
  }

  /**
   * Flag a pending update and offer the reload. Also the entry point for callers that have
   * already proved the build changed (a lazy chunk that 404s after a deploy), so pass
   * `message` to word that harder failure differently.
   *
   * No prompt on embedded routes — they're host-owned panels, so the update is left to the
   * route-boundary reload.
   */
  public promptReload(message = 'A new version of Semeris Docs is ready. Reload to get the latest.'): void {
    this.isUpdatePending.set(true);

    if (isEmbedded()) {
      return;
    }

    this.toastService.show({
      key: UPDATE_TOAST_KEY,
      position: 'top-center',
      color: 'info',
      duration: 0,
      dismissible: false,
      title: 'Update available',
      message,
      actions: [
        {
          label: 'Later',
          variant: 'ghost',
          onClick: () => this.snooze(),
        },
        {
          label: 'Reload',
          variant: 'primary',
          onClick: () => window.location.reload(),
        },
      ],
    });
  }

  public hardReset() {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  }

  /** Applies a pending update as a full page load on the next page-to-page navigation. */
  private watchRouteBoundaries(): void {
    this.router.events
      .pipe(
        filter((event): event is GuardsCheckEnd => event instanceof GuardsCheckEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        // Only `GuardsCheckEnd` proves every `CanDeactivate` passed — moving this earlier would reload over unsaved work.
        if (!event.shouldActivate || !this.isUpdatePending() || !this.canReloadAtBoundary() || this.isSnoozed()) {
          return;
        }

        // Query/fragment-only changes are in-page state (sort, filter, search).
        if (pathOf(event.urlAfterRedirects) === pathOf(this.router.url)) {
          return;
        }

        sessionStorage.setItem(BOUNDARY_RELOAD_SESSION_KEY, '1');
        window.location.assign(event.urlAfterRedirects);
      });
  }

  /** One boundary reload per session until a check confirms we landed on the new build. */
  private canReloadAtBoundary(): boolean {
    return sessionStorage.getItem(BOUNDARY_RELOAD_SESSION_KEY) !== '1';
  }

  private isSnoozed(): boolean {
    const snoozedAt = LocalStorageService.get<string>(SNOOZE_STORAGE_KEY);
    if (!snoozedAt) {
      return false;
    }

    return Math.abs(differenceInMinutes(new Date(), new Date(snoozedAt))) < SNOOZE_MINUTES;
  }

  private snooze(): void {
    LocalStorageService.set(new Date().toISOString(), SNOOZE_STORAGE_KEY);
  }
}
